import React, { useMemo, useState } from "react";
import styles from "./business-profile.module.css";
import { useSelector } from "react-redux";
import { activeCompanySelector } from "../../Reducers/activeCompanyReducer";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import { plansSelector } from "../../Reducers/plansReducer";
import { enterpriseDetailsSelector } from "../../Reducers/enterpriseDetailsReducer";
import { useFetchCardsQuery } from "../../Apis/cardApi";
import { useFetchAchQuery } from "../../Apis/achApi";
import {
  useAssignPaymentSourceMutation,
  useFetchPaymentSourcesQuery,
} from "../../Apis/paymentSourceApi";
import CustomSelect from "../CommonComponents/select/CustomSelect";

const NotManagedBilling = ({ activeCompanyId, cardsData, achData }) => {
  const [assignPaymentSource, { isLoading: isAssigningSource }] = useAssignPaymentSourceMutation();
  const [selectedSource, setSelectedSource] = useState(null);
  const [assignError, setAssignError] = useState("");

  const availableCards = useMemo(() => {
    if (!cardsData) return [];
    return cardsData.getCards();
  }, [cardsData]);

  const availableAchAccounts = useMemo(() => {
    if (!achData) return [];
    return achData.getBanks().filter((bank) => bank.isEnabled());
  }, [achData]);

  const availableSourceOptions = useMemo(() => {
    const cardOptions = availableCards.map((card) => card.toBillingSourceOption());
    const achOptions = availableAchAccounts.map((bank) => bank.toBillingSourceOption());
    return [...cardOptions, ...achOptions];
  }, [availableCards, availableAchAccounts]);

  const handleAttachBillingSource = async () => {
    if (!selectedSource || !activeCompanyId) return;
    setAssignError("");
    try {
      await assignPaymentSource({
        companyId: activeCompanyId,
        billingSource: "STRIPE",
        billingKeyType: selectedSource.type,
        billingKey: selectedSource.key,
      }).unwrap();
      setSelectedSource(null);
    } catch (error) {
      const message = error?.data?.errorMessage || "Failed to attach billing source. Please try again.";
      setAssignError(message);
    }
  };

  return (
    <div className={styles.billingAttachContainer}>
      <div className={styles.metaValue}>Billing not managed by enterprise</div>
      {availableSourceOptions.length > 0 ? (
        <>
          <div className={styles.billingAttachControls}>
            <div className={styles.billingAttachLabel}>Choose billing source to attach</div>
            <CustomSelect
              options={availableSourceOptions}
              selected={selectedSource?.value}
              label="Select card or bank account"
              onSelect={(value) => {
                const selectedOption = availableSourceOptions.find((option) => option.value === value) ?? null;
                setSelectedSource(selectedOption);
              }}
              wrapperClassName={styles.billingAttachSelectWrapper}
              selectClassName={styles.billingAttachSelect}
            />
          </div>
          <div className={styles.billingAttachControls}>
            {selectedSource && (
              <button
                type="button"
                className={styles.billingAttachButton}
                onClick={handleAttachBillingSource}
                disabled={isAssigningSource}
              >
                {isAssigningSource ? "Attaching..." : "Attach Billing Source"}
              </button>
            )}
          </div>
        </>
      ) : (
        <div className={styles.metaValue}>
          No available cards or bank accounts found.
        </div>
      )}
      {assignError && <div className={styles.billingAttachError}>{assignError}</div>}
    </div>
  );
};

const ManualBilling = () => (
  <div className={styles.metaValue}>
    Billing for this company is handled externally through manual processes outside of this system.
  </div>
);

const StripeBilling = ({ companyPaymentSource, cardsData, achData }) => {
  const availableCards = useMemo(() => {
    if (!cardsData) return [];
    return cardsData.getCards();
  }, [cardsData]);

  const availableAchAccounts = useMemo(() => {
    if (!achData) return [];
    return achData.getBanks().filter((bank) => bank.isEnabled());
  }, [achData]);

  let cardDetails = null;
  let achDetails = null;

  if (companyPaymentSource?.getBillingKeyType() === "CARD") {
    const billingKey = companyPaymentSource.getBillingKey();
    cardDetails = availableCards.find((card) => String(card.getId()) === String(billingKey));
  }

  if (companyPaymentSource?.getBillingKeyType() === "US_BANK_ACCOUNT") {
    const billingKey = companyPaymentSource.getBillingKey();
    achDetails = availableAchAccounts.find((bank) => String(bank.getId()) === String(billingKey));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div className={styles.metaValue}>
        Billing managed by you
      </div>
      <div className={styles.metaValue}>
        <span style={{ textDecoration: "underline" }}>Source</span>: {cardDetails ? (
          <>{cardDetails.getDisplayText()}</>
        ) : achDetails ? (
          <>{achDetails.getDisplayText()}</>
        ) : (
          <>Payment source details unavailable</>
        )}
      </div>
    </div>
  );
};

const OtherManagedBilling = () => (
  <div className={styles.metaValue}>
    Managed by someone else from enterprise
  </div>
);

const BillingTab = () => {
  const activeCompanyId = useSelector(activeCompanySelector);
  const { data: companies } = useFetchCompanyInfosQuery();
  const plans = useSelector(plansSelector);
  const enterpriseDetails = useSelector(enterpriseDetailsSelector);
  const { data: cardsData } = useFetchCardsQuery();
  const { data: achData } = useFetchAchQuery();
  const { data: paymentSources } = useFetchPaymentSourcesQuery();

  const company = companies?.getAccountDetailsByCompanyId(activeCompanyId);
  const currentPlanName = company ? plans?.getDisplayName(company.currentPlan) : "";
  const currentAdminId = enterpriseDetails?.id;

  // Find payment source for this company
  const companyPaymentSource = paymentSources?.getSources()?.find(
    source => source.getCompanyId() === activeCompanyId
  );

  const billingSource = companyPaymentSource?.getBillingSource();
  const administratorId = companyPaymentSource?.getAdministratorId();
  const isOwnAdmin = currentAdminId != null && administratorId != null && String(currentAdminId) === String(administratorId);

  return (
    <div className={styles.quickStatsRoot}>
      <div className={styles.kpiContainer}>
        <div className={styles.kpiColumn}>
          <div className={styles.kpiTitle}>Current Plan</div>
          <div className={styles.kpiValue}>{currentPlanName || "N/A"}</div>
        </div>
      </div>

      <div className={styles.sections}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>Billing Information</div>
          </div>
          
          {!billingSource ? (
            <NotManagedBilling
              activeCompanyId={activeCompanyId}
              cardsData={cardsData}
              achData={achData}
            />
          ) : billingSource === "MANUAL" ? (
            <ManualBilling />
          ) : isOwnAdmin ? (
            <StripeBilling
              companyPaymentSource={companyPaymentSource}
              cardsData={cardsData}
              achData={achData}
            />
          ) : (
            <OtherManagedBilling />
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingTab;

